import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBackendUrl } from './restaurant/RestaurantBackend';
import { HOMEPAGE_STATIC_MARKUP } from './homepageMarkup';
import { formatMobile } from './formatMobile';
import './Homepage.css';

// feat-153 marketing-site launch. Faithful port of the taste-approved mockup at
// references/mockup/index.html. The purely-presentational sections (hero -> team)
// are rendered verbatim from HOMEPAGE_STATIC_MARKUP; the interactive nav (Login +
// CTA), the real waitlist form (POST /api/waitlist), and the footer (Login + legal
// links) are real JSX below. All styling lives in Homepage.css, scoped under
// `.marketing-site` so the mockup's global reset does not leak into the app.

const INDUSTRIES = ['Restaurant', 'Retail', 'Hotel', 'Fitness', 'Skilled trades', 'Other'];

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Homepage: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    industry: 'Restaurant',
  });
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // feat-291 responsive nav. Below 980px .nav-links/.nav-cta are hidden (see
  // Homepage.css), so the header needs a real toggle + panel or nothing in it
  // is operable. State lives here because the panel is interactive JSX.
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const closeMenu = (returnFocus: boolean) => {
    setMenuOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  };

  // Scroll lock while the panel is open. The cleanup restores the previous
  // value, so unmounting mid-open cannot strand <body> at overflow: hidden,
  // and closing releases the lock before the browser acts on an in-page hash.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  // Focus moves into the panel on open; Escape closes it and hands focus back.
  useEffect(() => {
    if (!menuOpen) return undefined;
    panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const update = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const company = form.company.trim();
    const mobile = form.mobile.trim();
    // Optional: only send a mobile that actually contains a digit; a stray "+"
    // or punctuation-only value is treated as empty → null.
    const mobileToSend = /\d/.test(mobile) ? mobile : null;

    if (!name || !email || !company) {
      setErrorMsg('Please add your name, work email, and company.');
      setStatus('error');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setErrorMsg('Please enter a valid work email.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');
    try {
      // Restaurant-backend public/unauth endpoint. getBackendUrl() resolves the
      // configured host + protocol consistently across fake/local/dev/prod
      // (unauthedFetch is unusable here: its REACT_APP_API_URL base is unset by
      // the start/build scripts and points at the legacy interview API).
      const res = await fetch(getBackendUrl('/api/waitlist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          mobile: mobileToSend,
          company,
          industry: form.industry,
        }),
      });
      if (res.ok) {
        setStatus('success');
        return;
      }
      setErrorMsg(
        res.status === 429
          ? 'You are sending requests a little too quickly. Please try again in a moment.'
          : 'Something went wrong on our end. Please try again.',
      );
      setStatus('error');
    } catch {
      setErrorMsg('We could not reach the server. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <div className="marketing-site">
      {/* ============ NAV ============ */}
      <header className="nav">
        <div className="wrap nav-inner">
          <a className="brand" href="#top">
            <img src="/marketing/assets/logo_badge.png" alt="Bespoke" />
            <span>Bespoke</span>
          </a>
          <nav className="nav-links">
            <a href="#platform">Platform</a>
            <a href="#loop">The Loop</a>
            <a href="#case-study">Case study</a>
            <a href="#team">Advisors</a>
            <a href="#waitlist">Waitlist</a>
          </nav>
          <div className="nav-cta">
            {/* Login path preserved (spec GATE) — React route, use Link. */}
            <Link to="/scorecard-login" className="nav-login">
              Log in
            </Link>
            <a href="#waitlist" className="btn btn-primary">
              Join the waitlist
            </a>
          </div>
          {/* Hidden above 980px by CSS; the only operable nav control below it. */}
          <button
            type="button"
            className="nav-toggle"
            ref={toggleRef}
            aria-expanded={menuOpen}
            aria-controls="nav-panel"
            aria-label={menuOpen ? 'Close menu' : 'Menu'}
            onClick={() => (menuOpen ? closeMenu(true) : setMenuOpen(true))}
          >
            <span className="bars" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>
        </div>

        {/* Inside <header>, outside .nav-inner: absolutely positioned against
            the sticky header rather than being a flex child of the row. */}
        <div
          className={menuOpen ? 'nav-panel open' : 'nav-panel'}
          id="nav-panel"
          ref={panelRef}
        >
          <div className="wrap">
            <a href="#platform" onClick={() => closeMenu(false)}>
              Platform
            </a>
            <a href="#loop" onClick={() => closeMenu(false)}>
              The Loop
            </a>
            <a href="#case-study" onClick={() => closeMenu(false)}>
              Case study
            </a>
            <a href="#team" onClick={() => closeMenu(false)}>
              Advisors
            </a>
            <a href="#waitlist" onClick={() => closeMenu(false)}>
              Waitlist
            </a>
            <div className="panel-divider" />
            {/* Login path preserved (spec GATE) — React route, use Link. */}
            <Link to="/scorecard-login" className="panel-login" onClick={() => closeMenu(false)}>
              Log in
            </Link>
            <a href="#waitlist" className="btn btn-primary" onClick={() => closeMenu(false)}>
              Join the waitlist
            </a>
          </div>
        </div>
      </header>

      {/* Sibling of <header>, never a child: .nav carries backdrop-filter,
          which would make it the containing block for this fixed element and
          collapse inset: 0 to the 72px header box. */}
      <div
        className={menuOpen ? 'nav-scrim open' : 'nav-scrim'}
        hidden={!menuOpen}
        onClick={() => closeMenu(true)}
      />

      <main id="top">
        {/* Static, presentational sections (hero -> team) ported verbatim. */}
        <div dangerouslySetInnerHTML={{ __html: HOMEPAGE_STATIC_MARKUP }} />

        {/* ============ WAITLIST ============ */}
        <section className="waitlist" id="waitlist">
          <div className="wrap">
            <div className="wl-band">
              <span className="wl-eyebrow">Get early access</span>
              <h2>
                Grow your revenue. <span className="accent">Run the loop.</span>
              </h2>
              <p className="wl-intro">
                Bespoke is launching soon. Gamify your training, measure live service, and test
                your strategies. Join the waitlist and we will reach out as we roll out.
              </p>

              {status !== 'success' && (
                <form className="wl-form" id="waitlistForm" onSubmit={handleSubmit} noValidate>
                  <div className="row2">
                    <div className="wl-field">
                      <label htmlFor="wl-name">Name</label>
                      <input
                        id="wl-name"
                        name="name"
                        type="text"
                        placeholder="Jane Operator"
                        value={form.name}
                        onChange={update('name')}
                        required
                      />
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-email">Work email</label>
                      <input
                        id="wl-email"
                        name="email"
                        type="email"
                        placeholder="jane@yourcompany.com"
                        value={form.email}
                        onChange={update('email')}
                        required
                      />
                    </div>
                  </div>
                  <div className="row2">
                    <div className="wl-field">
                      <label htmlFor="wl-mobile">Mobile number</label>
                      <input
                        id="wl-mobile"
                        name="mobile"
                        type="tel"
                        inputMode="tel"
                        maxLength={32}
                        placeholder="(555) 123-4567"
                        value={form.mobile}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, mobile: formatMobile(e.target.value) }))
                        }
                      />
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-company">Company</label>
                      <input
                        id="wl-company"
                        name="company"
                        type="text"
                        placeholder="Your business"
                        value={form.company}
                        onChange={update('company')}
                        required
                      />
                    </div>
                  </div>
                  <div className="wl-field">
                    <label htmlFor="wl-industry">Industry</label>
                    <select
                      id="wl-industry"
                      name="industry"
                      value={form.industry}
                      onChange={update('industry')}
                    >
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Joining…' : 'Join the waitlist'}
                  </button>
                  {status === 'error' && (
                    <p className="wl-error" role="alert">
                      {errorMsg}
                    </p>
                  )}
                  <p className="wl-note">We will only use this to contact you about early access.</p>
                </form>
              )}

              {status === 'success' && (
                <div className="wl-ok" style={{ display: 'block' }}>
                  <div className="check">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 12l5 5L20 6" />
                    </svg>
                  </div>
                  <h3 style={{ color: '#fff' }}>You are on the list.</h3>
                  <p style={{ color: 'var(--footer-ink)' }}>
                    Thanks for joining. We will be in touch as we roll out.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer className="footer">
        <div className="wrap">
          <div className="footer-top">
            <div>
              <a className="brand" href="#top">
                <img
                  src="/marketing/assets/logo_badge.png"
                  alt="Bespoke"
                  style={{ width: 30, height: 30 }}
                />
                <span>Bespoke</span>
              </a>
              <p className="fblurb">
                Bespoke is the revenue and guest experience platform for in-person commerce.
                We measure the last ten feet, the live conversation between your team and your
                customers, and turn it into a loop that grows revenue, starting with restaurants.
              </p>
            </div>
            <div className="fcol">
              <h4>Platform</h4>
              <a href="#loop">The Loop</a>
              <a href="#platform">Strategy</a>
              <a href="#platform">Train</a>
              <a href="#platform">Measure</a>
              <a href="#platform">Test</a>
            </div>
            <div className="fcol">
              <h4>Company</h4>
              <a href="#team">Advisors</a>
              <a href="#waitlist">Waitlist</a>
              {/* Login path preserved (spec GATE) — reachable on mobile where the
                  nav CTA is hidden. React route, use Link. */}
              <Link to="/scorecard-login">Log in</Link>
            </div>
            <div className="fcol">
              <h4>Legal</h4>
              {/* Existing static pages in public/ — plain anchors, not router links. */}
              <a href="/privacy.html">Privacy Policy</a>
              <a href="/terms.html">Terms of Service</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="copy">© 2026 Bespoke. All rights reserved.</span>
            <div className="accel">
              <span className="lab">Accelerated by</span>
              <span className="chip-logo">
                <img src="/marketing/assets/logos/deepgram.png" alt="Deepgram" />
              </span>
              <span className="chip-logo">
                <img src="/marketing/assets/logos/nvidia.png" alt="NVIDIA Inception" />
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
